import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useApolloClient } from '@apollo/react-hooks';
import { debounce } from 'debounce';
// components
import { Spinner, SideBarMenu } from '@ems/client-design-system';
import { PageHeader, PowerBi } from 'src/components';
// reducers
import { useLanguageSelectors } from 'src/app/reducers';
// resolvers
import { fetchUserDashboards, fetchDashboardAccess } from 'src/@dashboard/resolvers/dataResolver';
// functions
import { useWindowSize } from 'src/app/functions/windowSize';
// constants
import { DEVICE_TYPES, POWERBI } from 'src/constants';
import { setTabTitle } from 'src/utils';

export const ContentContainer = ({
  selectedReport = null,
}: {
  selectedReport?: null | { id: string; name: string };
}) => {
  const client = useApolloClient();
  // window height
  const { width, height } = useWindowSize();
  // TODO: Once all pages are mobile-friendly then use this logic to switch to mobile view when screen width smaller that the defined breakpoint
  // const mobileMode = isMobile || width < MOBILE_SCREEN_WIDTH;
  const deviceType = isMobile ? DEVICE_TYPES.MOBILE : DEVICE_TYPES.DESKTOP;

  const defaultAccess = {
    id: '',
    embedReportId: '',
    embedUrl: '',
    embedToken: '',
    expiration: '',
  };

  // Translation
  const languageSelectors = useLanguageSelectors();
  const {
    components: {
      headings: { dashboard: pageTitle },
      hints: { noReportsFound },
      errors: { contactSupportShort },
    },
  } = languageSelectors.getLanguage();

  setTabTitle(pageTitle);

  const [loading, updateLoading] = useState<boolean>(false);
  const [userDashboards, setUserDashboards] = useState([]); // dashboards for desktop mode or both
  const [powerBiAccess, setPowerBiAccess] = useState(defaultAccess);
  const [layoutType, setLayoutType] = useState(POWERBI.LAYOUT_DESKTOP);
  const isReportAvailable = Array.isArray(userDashboards) && userDashboards.length;
  const getReportHeight = (height: number) => {
    const iframeHeight = isMobile ? 0 : 648;
    const paddingBottom = isMobile ? 64 : 90;
    return `${height >= iframeHeight ? height - paddingBottom : iframeHeight - paddingBottom}px`;
  };

  const displayReport = debounce((id: string) => {
    if (!isMobile && powerBiAccess.id === id) {
      return; // ignore and don't reload. Report already loaded
    }
    updateLoading(true);
    fetchDashboardAccess(client, id).then(({ embedReportId, embedUrl, embedToken, expiration }) => {
      setPowerBiAccess(defaultAccess);
      setPowerBiAccess({
        id,
        embedReportId,
        embedUrl,
        embedToken,
        expiration,
      });
    });
  }, 500);

  useEffect(() => {
    if (!userDashboards.length) {
      fetchUserDashboards(client).then(data => {
        const modeArr = [deviceType, 'Any'];
        const selection = data.filter(({ mode }) => modeArr.includes(mode));
        setUserDashboards(selection);
        if (selection && selection.length) {
          const [firstReport] = selection;
          displayReport(firstReport.id);
        }
      });
    }
  }, [deviceType]);

  // Set layout type for the PowerBI reports.
  useEffect(() => {
    if (userDashboards && deviceType && powerBiAccess.id !== '') {
      const selectedDashboard = userDashboards.find(dashboard => dashboard.id == powerBiAccess.id);
      const selectedLayout =
        deviceType === 'Mobile' && selectedDashboard.mode === 'Mobile'
          ? POWERBI.LAYOUT_MOBILE
          : POWERBI.LAYOUT_DESKTOP;
      setLayoutType(selectedLayout);
    }
  }, [userDashboards, powerBiAccess]);

  useEffect(() => {
    if (isMobile && selectedReport) {
      displayReport(selectedReport.id);
    }
  }, [selectedReport]);

  if (!isMobile && !userDashboards) {
    return <PageHeader title={pageTitle} />;
  }

  if (!isMobile && !isReportAvailable) {
    return (
      <PageHeader title={pageTitle}>
        <p className="page-header-description">
          {noReportsFound}
          <br />
          {contactSupportShort}
        </p>
      </PageHeader>
    );
  }

  return (
    <div className="layout_content">
      {!isMobile && (
        <div className="sidebar">
          <SideBarMenu
            content={userDashboards}
            onSelect={({ id }) => {
              displayReport(id);
            }}
          />
        </div>
      )}
      <div
        className="content"
        style={
          isMobile
            ? { width, height: getReportHeight(height) }
            : { height: getReportHeight(height) }
        }>
        {loading && (
          <div className="loading-wrapper">
            <Spinner loading={loading} size="l" centered={true} />
          </div>
        )}
        {powerBiAccess.embedReportId && (
          <PowerBi
            id={powerBiAccess.embedReportId}
            embedUrl={powerBiAccess.embedUrl}
            accessToken={powerBiAccess.embedToken}
            layoutType={layoutType}
            loaded={() => {
              updateLoading(false);
            }}
          />
        )}
      </div>
    </div>
  );
};
